/**
 * Display formatting.
 *
 * The currency helpers matter more than they look: balances arrive from the
 * API as **decimal strings**, because they are `bigint` minor units in
 * Postgres and JSON has no bigint. Parsing one into a `number` to format it is
 * how a wallet display starts disagreeing with the ledger.
 */

import { panelTimeZone } from "./timezone";

/** Soft/hard currency — whole units, no decimals. Input is a decimal string. */
export function formatCurrency(units: string): string {
  const negative = units.startsWith("-");
  const digits = negative ? units.slice(1) : units;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return negative ? `-${grouped}` : grouped;
}

/** Real money, stored in the smallest unit (paise, cents). */
export function formatMoney(minorUnits: string, currency: "INR" | "USD"): string {
  const value = Number(minorUnits) / 100;
  return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
    style: "currency",
    currency,
  }).format(value);
}

export function formatCompact(value: number): string {
  return new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 }).format(
    value,
  );
}

export function formatPercent(value: number, digits = 1): string {
  return `${(value * 100).toFixed(digits)}%`;
}

/** Match durations. `4:32` reads faster than `272s` when scanning a column. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Timestamps render in the panel's zone — the admin's saved one, else the
 * browser's (see `lib/timezone`).
 *
 * Always from a UTC `*_at`, never from a `*_local`: `new Date()` keeps the
 * instant of a `*_local` but drops its offset, so formatting one is only right
 * by coincidence. The zone is the same one the request went out with, so this
 * agrees with the `*_local` copy the API sent back.
 */
export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: panelTimeZone(),
  }).format(new Date(iso));
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeZone: panelTimeZone(),
  }).format(new Date(iso));
}

/** Like `formatDateTime`, for a nullable field. */
export function formatMaybeDateTime(iso: string | null, fallback = "—"): string {
  return iso ? formatDateTime(iso) : fallback;
}

/** Shortens a UUID for display. Full value belongs in a copy button. */
export function shortId(id: string): string {
  return id.slice(0, 8);
}
